const SECTION_SELECTOR = '[data-custom-video-section]';
const VIDEO_SELECTOR = '[data-custom-video]';
const CONTROL_SELECTOR = '[data-custom-video-control]';
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

let mediaQuery;
let observer;
let listenersBound = false;

function getSections() {
  return Array.from(document.querySelectorAll(SECTION_SELECTOR));
}

function getVideos(section) {
  return Array.from(section.querySelectorAll(VIDEO_SELECTOR));
}

function getControl(section) {
  return section.querySelector(CONTROL_SELECTOR);
}

function setSectionState(section, isPlaying) {
  const control = getControl(section);

  section.dataset.customVideoState = isPlaying ? 'playing' : 'paused';

  if (isPlaying) {
    section.dataset.customVideoControls = 'hidden';
  } else {
    section.dataset.customVideoControls = 'visible';
  }

  if (!control) return;

  control.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
  control.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
}

function pauseVideo(video) {
  try {
    video.pause();
  } catch (error) {}
}

function prepareVideo(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.removeAttribute('autoplay');
}

function playVideo(video, section) {
  if (!video || video.offsetParent === null) return;

  prepareVideo(video);

  if (video.readyState === 0) {
    try {
      video.load();
    } catch (error) {}
  }

  const playPromise = video.play();

  if (playPromise && typeof playPromise.then === 'function') {
    playPromise.then(() => setSectionState(section, true)).catch(() => setSectionState(section, false));
    return;
  }

  setSectionState(section, true);
}

function getActiveVideo(section) {
  const selector = mediaQuery.matches ? '[data-video-role="mobile"]' : '[data-video-role="desktop"]';

  return section.querySelector(selector) || section.querySelector(VIDEO_SELECTOR);
}

function pauseSection(section, updateState = true) {
  getVideos(section).forEach(pauseVideo);

  if (updateState) {
    setSectionState(section, false);
  }
}

function syncSection(section) {
  const videos = getVideos(section);

  if (!videos.length) return;

  if (document.visibilityState === 'hidden' || section.dataset.customVideoInView === 'false') {
    pauseSection(section);
    return;
  }

  const activeVideo = getActiveVideo(section);

  videos.forEach((video) => {
    if (video !== activeVideo) pauseVideo(video);
  });

  if (!activeVideo || activeVideo.offsetParent === null) return;

  if (section.dataset.customVideoState === 'playing') {
    playVideo(activeVideo, section);
    return;
  }

  pauseVideo(activeVideo);
  setSectionState(section, false);
}

function syncAllSections() {
  getSections().forEach(syncSection);
}

function bindSectionControls(section) {
  if (section.dataset.customVideoBound === 'true') return;

  const control = getControl(section);
  const videos = getVideos(section);

  if (control) {
    control.addEventListener('click', (event) => {
      event.stopPropagation();
      control.blur();

      const activeVideo = getActiveVideo(section);

      if (section.dataset.customVideoState === 'playing') {
        pauseSection(section);
        return;
      }

      videos.forEach((video) => {
        if (video !== activeVideo) pauseVideo(video);
      });

      playVideo(activeVideo, section);
    });
  }

  section.addEventListener('click', (event) => {
    if (event.target.closest(CONTROL_SELECTOR)) return;
    if (section.dataset.customVideoState !== 'playing') return;

    pauseSection(section);
  });

  videos.forEach((video) => {
    video.addEventListener('play', () => {
      videos.forEach((otherVideo) => {
        if (otherVideo !== video) pauseVideo(otherVideo);
      });

      setSectionState(section, true);
    });

    video.addEventListener('pause', () => {
      if (video === getActiveVideo(section) && video.paused) {
        setSectionState(section, false);
      }
    });

    video.addEventListener('ended', () => setSectionState(section, false));
  });

  setSectionState(section, false);
  section.dataset.customVideoBound = 'true';
}

function ensureObserver() {
  if (observer || !('IntersectionObserver' in window)) return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.customVideoInView = entry.isIntersecting ? 'true' : 'false';
        syncSection(entry.target);
      });
    },
    { threshold: 0.15 },
  );
}

function observeSection(section) {
  if (section.dataset.customVideoObserved === 'true') return;

  if ('IntersectionObserver' in window) {
    ensureObserver();
    section.dataset.customVideoInView = 'false';
    section.dataset.customVideoObserved = 'true';
    observer.observe(section);
    return;
  }

  section.dataset.customVideoInView = 'true';
  section.dataset.customVideoObserved = 'true';
}

function bindListeners() {
  if (listenersBound) return;

  mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  const handleChange = () => syncAllSections();

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(handleChange);
  }

  document.addEventListener('visibilitychange', handleChange);
  document.addEventListener('shopify:section:load', () => {
    initCustomVideo();
  });
  window.addEventListener('pageshow', handleChange);
  window.addEventListener('resize', handleChange, { passive: true });

  listenersBound = true;
}

export function initCustomVideo() {
  const sections = getSections();

  if (!sections.length) return;

  bindListeners();

  sections.forEach((section) => {
    bindSectionControls(section);
    observeSection(section);
  });

  syncAllSections();
}
